import type { FieldMeta, FieldOption } from "@wms/domain";

/**
 * KZ 30.10.2026 (ТЗ реквизитов §§5–6): the quick checks the form repeats for convenience — the server applies the same
 * policy (`common/kz.py`) to every channel and decides. The words are the server's, a value is never repeated back.
 */

const TAX_ID_LABELS: Record<string, string> = { too: "БИН", llc: "БИН", state: "БИН", ie: "ИИН", individual: "ИИН" };

/** «БИН» for a ТОО, «ИИН» for an ИП, «БИН/ИИН» when the form is not known — never «ИНН». */
export function taxIdLabel(kind: unknown): string {
  return TAX_ID_LABELS[String(kind ?? "")] ?? "БИН/ИИН";
}

/** The check digit of a БИН / ИИН by the rules of 2023: weights 1…11 modulo 11, a 10 is recounted with 3…11, 1, 2. */
export function taxIdCheckDigit(firstEleven: string): number | null {
  const digits = [...firstEleven].map(Number);
  const first = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const second = [3, 4, 5, 6, 7, 8, 9, 10, 11, 1, 2];
  let remainder = digits.reduce((sum, d, i) => sum + d * first[i], 0) % 11;
  if (remainder === 10) {
    remainder = digits.reduce((sum, d, i) => sum + d * second[i], 0) % 11;
    if (remainder === 10) return null;
  }
  return remainder;
}

export function taxIdChecksumValid(value: string): boolean {
  if (!/^\d{12}$/.test(value)) return false;
  return taxIdCheckDigit(value.slice(0, 11)) === Number(value[11]);
}

/** «ИИН должен содержать 12 цифр (сейчас 11)» — the first problem of a typed identifier, null when fine or empty. */
export function taxIdProblem(value: unknown, kind: unknown): string | null {
  const text = typeof value === "string" ? value.trim() : value === null || value === undefined ? "" : String(value);
  if (!text) return null;
  const label = taxIdLabel(kind);
  if (!/^\d+$/.test(text)) return `${label} должен содержать только цифры: 12 цифр без пробелов, букв и знаков`;
  if (text.length !== 12) return `${label} должен содержать 12 цифр (сейчас ${text.length})`;
  if (!taxIdChecksumValid(text)) return `${label} не проходит проверку контрольной цифры: сверьте цифры с регистрационным документом`;
  return null;
}

/** ISO 13616: the first four characters moved to the end, letters as 10…35, the number modulo 97 equals 1. */
export function ibanValid(value: string): boolean {
  const moved = value.slice(4) + value.slice(0, 4);
  let remainder = 0;
  for (const ch of moved) {
    const code = parseInt(ch, 36);
    if (Number.isNaN(code)) return false;
    for (const digit of String(code)) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder === 1;
}

const compact = (value: unknown) => String(value ?? "").replace(/\s/g, "").toUpperCase();

export function iikProblem(value: unknown): string | null {
  const text = compact(value);
  if (!text) return null;
  if (!/^KZ\d{2}[0-9A-Z]{16}$/.test(text)) return `Проверьте ИИК: казахстанский счёт начинается с KZ и содержит 20 символов (сейчас ${text.length})`;
  if (!ibanValid(text)) return "ИИК не проходит проверку контрольных цифр (IBAN, ISO 13616): сверьте счёт со справкой банка";
  return null;
}

export function bicProblem(value: unknown): string | null {
  const text = compact(value);
  if (!text) return null;
  const match = /^[A-Z]{4}([A-Z]{2})[0-9A-Z]{2}([0-9A-Z]{3})?$/.exec(text);
  if (!match) return "Проверьте БИК банка: 8 латинских букв и цифр, например HSBKKZKX (11 — с кодом филиала)";
  if (match[1] !== "KZ") return `БИК банка Казахстана содержит код страны KZ в 5–6 знаках, а здесь «${match[1]}»: проверьте банк`;
  return null;
}

export function kbeProblem(value: unknown): string | null {
  const text = String(value ?? "").trim();
  if (!text) return null;
  if (!/^[12][1-9]$/.test(text)) return "КБе — 2 цифры: первая 1 (резидент) или 2 (нерезидент), вторая — сектор экономики от 1 до 9, например 17";
  return null;
}

/** The problem of a value by the `check` of its field (meta); `kind` names the form for the tax identifier. */
export function checkProblem(check: FieldMeta["check"], value: unknown, kind?: unknown): string | null {
  switch (check) {
    case "tax_id":
      return taxIdProblem(value, kind);
    case "iik":
      return iikProblem(value);
    case "bic":
      return bicProblem(value);
    case "kbe":
      return kbeProblem(value);
    default:
      return null;
  }
}

/**
 * R02: the options a control offers — the current ones, plus the historical value the saved record already has
 * («ТОО / ООО (прежняя форма)»); a saved value that is no option at all is shown as it is («прежнее значение»).
 */
export function visibleOptions(options: FieldOption[] | null | undefined, saved: unknown): FieldOption[] {
  const list = options ?? [];
  const current = saved === null || saved === undefined || saved === "" ? null : String(saved);
  const out = list.filter((o) => !o.legacy || o.value === current);
  if (current !== null && !list.some((o) => o.value === current)) out.push({ value: current, label: `${current} (прежнее значение)`, legacy: true });
  return out;
}

/** The label of a field that follows another one: «БИН» when «ТОО» is chosen. */
export function labelFor(field: Pick<FieldMeta, "label" | "label_by">, byValue: unknown): string {
  const spec = field.label_by;
  if (!spec) return field.label;
  return spec.map[String(byValue ?? "")] ?? spec.default ?? field.label;
}

export function helpFor(field: Pick<FieldMeta, "help" | "label_by">, byValue: unknown): string | null {
  return field.label_by?.help?.[String(byValue ?? "")] ?? field.help ?? null;
}

/**
 * §5.2: the warning after a change of the form while the identifier is filled — the identifier is never rewritten.
 * `before` is the form the identifier was typed (or saved) for.
 */
export function switchNotice(field: Pick<FieldMeta, "notice_on_change">, before: unknown, now: unknown, target: unknown): string | null {
  const spec = field.notice_on_change;
  const filled = target !== null && target !== undefined && String(target).trim() !== "";
  if (!spec || !filled || before === undefined || String(before ?? "") === String(now ?? "")) return null;
  return spec.map[String(now ?? "")] ?? null;
}
